import React, { useEffect, useMemo, useRef, useState } from "react";
import { Eye, RotateCw, X, Lock } from "lucide-react";
import { api } from "./api";
import { Badge, Puces, Recherche, Section } from "./ui";

/* Simulateur de l'app (école, 20.10.2026) : « je veux une simulation de l'application pour
   chaque profil — avec un téléphone, les familles / profs / école à choisir par nom —
   cliquer dans l'application et voir ce qu'ils voient ».

   La console demande un jeton de simulation (une heure, lecture seule) et ouvre la VRAIE
   app dans un téléphone dessiné. Le jeton est posé sur cette fenêtre (__simulationApp) :
   l'app du cadre le lit au démarrage (frontend/src/lib/simulation.js), range ses clés en
   mémoire et marque chaque appel ; le serveur refuse toute écriture. */

const PROFILS = [
  { valeur: "famille", label: "Familles membres", liste: "familles", nom: "famille" },
  { valeur: "prof", label: "Profs", liste: "profs", nom: "prof" },
  { valeur: "ecole", label: "École (mode école)", liste: "ecole", nom: "école" },
];
/* Où en est la personne avec l'app (comme Application → Qui a l'app). « App installée » :
   le téléphone montre l'app telle qu'elle la voit depuis son icône (sans « Installer
   l'application » ni « Retour au site ») ; sinon, la version du navigateur. */
const ETATS = {
  app: { label: "App installée", ton: "vert" },
  site: { label: "Navigateur", ton: "bleu" },
  a_prouver: { label: "Adresse à prouver", ton: "ambre" },
  sans_compte: { label: "Pas de compte", ton: "neutre" },
  jamais: { label: "Jamais ouvert", ton: "neutre" },
};
const simple = (s) => String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
const heure = (ms) => new Date(ms).toLocaleTimeString("fr-CH", { hour: "2-digit", minute: "2-digit" });

/* La taille d'un vrai iPhone (écran de 390 × 844 points, iPhone 12 à 15 — école,
   20.10.2026 : « la taille du téléphone n'est pas alignée avec la réalité, il est moins
   haut qu'un téléphone »). L'app s'y met en page exactement comme sur le téléphone : la
   barre d'état (heure, encoche) en haut, l'app dessous. Si la fenêtre est trop basse ou
   trop étroite, le téléphone ENTIER est réduit, jamais raccourci : les proportions restent
   celles d'un vrai téléphone. */
const ECRAN = { largeur: 390, hauteur: 844 };
const BORD = 12;
const BARRE_ETAT = 47;
const CADRE = { largeur: ECRAN.largeur + 2 * BORD, hauteur: ECRAN.hauteur + 2 * BORD };

function useEchelle(ref) {
  const [echelle, setEchelle] = useState(1);
  useEffect(() => {
    const calculer = () => {
      const largeur = ref.current?.parentElement?.clientWidth || CADRE.largeur;
      // La place sous l'en-tête de la console (sa barre du haut et un peu d'air).
      const hauteur = window.innerHeight - 110;
      setEchelle(Math.max(0.5, Math.min(1, largeur / CADRE.largeur, hauteur / CADRE.hauteur)));
    };
    calculer();
    window.addEventListener("resize", calculer);
    const parent = ref.current?.parentElement;
    const obs = parent && typeof ResizeObserver !== "undefined" ? new ResizeObserver(calculer) : null;
    if (obs) obs.observe(parent);
    return () => { window.removeEventListener("resize", calculer); if (obs) obs.disconnect(); };
  }, [ref]);
  return echelle;
}

function Telephone({ vue, cle, termine }) {
  const ref = useRef(null);
  const e = useEchelle(ref);
  return (
    <div>
      <div ref={ref} className="mx-auto" data-testid="simulateur-telephone"
        style={{ width: CADRE.largeur * e, height: CADRE.hauteur * e }}>
        <div style={{ width: CADRE.largeur, height: CADRE.hauteur, padding: BORD, borderRadius: 58,
          background: "#0F172A", boxShadow: "0 24px 60px -20px rgba(15,23,42,.45)",
          transform: `scale(${e})`, transformOrigin: "top left" }}>
          <div className="relative flex flex-col"
            style={{ width: ECRAN.largeur, height: ECRAN.hauteur, borderRadius: 46, overflow: "hidden", background: "#F8FAFC" }}>
            {/* La barre d'état : l'heure, l'encoche, le réseau — l'app commence dessous,
                comme sur un iPhone (zone de sécurité du haut). */}
            <div className="relative flex items-center justify-between px-8 text-[15px] font-semibold shrink-0"
              style={{ height: BARRE_ETAT, background: "#fff", color: "#0F172A" }} aria-hidden="true">
              <span>{heure(Date.now())}</span>
              <span className="absolute left-1/2 -translate-x-1/2"
                style={{ top: 11, width: 120, height: 34, borderRadius: 18, background: "#0F172A" }} />
              <span style={{ letterSpacing: 1, fontSize: 11 }}>●●●</span>
            </div>
            {vue && !termine ? (
              <iframe key={cle} src={vue.chemin} title={`L'app vue par ${vue.nom}`}
                data-testid="simulateur-ecran"
                className="flex-1 w-full" style={{ border: 0 }} />
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-center px-10 gap-3"
                data-testid="simulateur-vide">
                <Eye size={28} style={{ color: "#94A3B8" }} />
                <p className="text-sm text-[var(--muted)]">
                  {termine
                    ? "La simulation est terminée (une heure). « Rouvrir » la relance pour la même personne."
                    : "Choisis une famille, un prof ou un compte de l'école : l'app s'ouvre ici, comme sur son téléphone."}
                </p>
              </div>
            )}
            {/* La barre d'accueil de l'iPhone, par-dessus l'app comme sur le vrai téléphone. */}
            <span className="absolute left-1/2 -translate-x-1/2 pointer-events-none" aria-hidden="true"
              style={{ bottom: 7, width: 134, height: 5, borderRadius: 3, background: "rgba(15,23,42,.85)" }} />
          </div>
        </div>
      </div>
      <p className="text-[11px] text-[var(--muted)] text-center mt-2" data-testid="simulateur-taille">
        {e < 0.995
          ? `Taille d'un iPhone (390 × 844), réduite à ${Math.round(e * 100)} % pour tenir dans la fenêtre.`
          : "Taille réelle d'un iPhone (390 × 844)."}
      </p>
    </div>
  );
}

export function Simulateur() {
  const [personnes, setPersonnes] = useState(null);
  const [erreur, setErreur] = useState("");
  const [profil, setProfil] = useState("famille");
  const [q, setQ] = useState("");
  const [vue, setVue] = useState(null);
  const [cle, setCle] = useState(0);
  const [ouverture, setOuverture] = useState("");
  const [maintenant, setMaintenant] = useState(Date.now());

  useEffect(() => {
    api("/simulateur/personnes").then(setPersonnes).catch((e) => setErreur(e.message));
    const minuterie = setInterval(() => setMaintenant(Date.now()), 30_000);
    // En quittant l'écran, la console oublie le jeton : aucun cadre ne peut plus le lire.
    return () => { clearInterval(minuterie); delete window.__simulationApp; };
  }, []);

  const def = PROFILS.find((p) => p.valeur === profil);
  const liste = useMemo(() => {
    const tous = (personnes && personnes[def.liste]) || [];
    const mots = simple(q).split(/\s+/).filter(Boolean);
    return tous.filter((p) => mots.every((m) => simple(`${p.nom} ${p.detail} ${p.ident}`).includes(m)));
  }, [personnes, def, q]);

  const ouvrir = async (p, quel = profil) => {
    setOuverture(p.ident);
    setErreur("");
    try {
      const r = await api("/simulateur/ouvrir", { method: "POST", body: JSON.stringify({ profil: quel, ident: p.ident }) });
      window.__simulationApp = { jeton: r.jeton, profil: quel, nom: r.nom, installee: p.etat === "app" };
      // Sur un petit écran, le téléphone est sous la liste : on y descend.
      if (window.innerWidth < 1024) {
        setTimeout(() => document.querySelector("[data-testid=simulateur-bandeau]")
          ?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
      }
      setVue({ ...r, profil: quel, personne: p, fin: Date.now() + r.minutes * 60_000 });
      setMaintenant(Date.now());
      setCle((k) => k + 1);
    } catch (e) {
      setErreur(e.message);
    } finally {
      setOuverture("");
    }
  };
  const fermer = () => { delete window.__simulationApp; setVue(null); };
  const termine = !!vue && maintenant >= vue.fin;
  const nomProfil = (v) => PROFILS.find((p) => p.valeur === v)?.nom || v;

  return (
    <Section titre="Simulateur de l'app"
      aide="Vois l'app exactement comme une famille, un prof ou un compte de l'école la voit, et clique dans ses menus. Lecture seule : rien n'est enregistré ni envoyé, et la visite n'est pas comptée dans ses connexions."
      testid="app-simulateur">
      {erreur && <p className="text-sm" style={{ color: "#B91C1C" }} data-testid="simulateur-erreur">{erreur}</p>}
      <div className="grid gap-6 lg:grid-cols-[minmax(0,380px)_minmax(0,1fr)] items-start">
        <div className="bg-white rounded-2xl border p-4 space-y-3" style={{ borderColor: "var(--border)" }}>
          <Puces testid="simulateur-profils" valeur={profil} onChange={(v) => { setProfil(v); setQ(""); }}
            options={PROFILS.map((p) => ({ valeur: p.valeur, label: p.label, testid: `simulateur-profil-${p.valeur}`,
              nb: personnes ? (personnes[p.liste] || []).length : undefined }))} />
          <Recherche valeur={q} onChange={setQ} largeur="100%" testid="simulateur-recherche"
            placeholder={profil === "famille" ? "Nom du parent ou d'un enfant…" : "Nom…"} />
          {!personnes ? (
            <p className="text-sm text-[var(--muted)]">{erreur ? "" : "Chargement…"}</p>
          ) : (
            <div className="overflow-y-auto -mx-1" style={{ maxHeight: 460 }} data-testid="simulateur-liste">
              {liste.length === 0 && <p className="text-sm text-[var(--muted)] px-1 py-2">Personne ne correspond.</p>}
              {liste.slice(0, 80).map((p) => {
                const actif = vue && vue.profil === profil && vue.personne?.ident === p.ident;
                return (
                  <button key={p.ident} type="button" onClick={() => ouvrir(p)} disabled={!!ouverture}
                    data-testid="simulateur-personne"
                    className="w-full text-left rounded-xl px-3 py-2.5 flex items-center gap-3 transition-colors"
                    style={{ background: actif ? "#ECFEFF" : "transparent",
                      border: `1px solid ${actif ? "var(--primary)" : "transparent"}` }}>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold truncate">{p.nom}</span>
                      {p.detail && <span className="block text-xs text-[var(--muted)] truncate">{p.detail}</span>}
                    </span>
                    {ETATS[p.etat] && <Badge ton={ETATS[p.etat].ton}>{ETATS[p.etat].label}</Badge>}
                    {ouverture === p.ident && <span className="text-xs text-[var(--muted)]">…</span>}
                  </button>
                );
              })}
              {liste.length > 80 && (
                <p className="text-xs text-[var(--muted)] px-1 py-2">… et {liste.length - 80} autres : affine la recherche.</p>
              )}
            </div>
          )}
        </div>

        <div className="space-y-3 min-w-0 lg:sticky lg:top-20" data-testid="simulateur-colonne">
          {vue && (
            <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="simulateur-bandeau">
              <span>Tu vois l'app comme <strong>{vue.nom}</strong> ({nomProfil(vue.profil)}
                {vue.personne?.etat === "app" ? ", depuis l'app installée" : vue.personne?.etat ? ", dans un navigateur" : ""})</span>
              <Badge ton="ambre"><Lock size={11} className="inline -mt-0.5 mr-1" />lecture seule</Badge>
              <span className="text-xs text-[var(--muted)]">
                {termine ? "terminée" : `jusqu'à ${heure(vue.fin)}`}
              </span>
              <span className="ml-auto flex gap-2">
                <button type="button" className="btn-ghost text-xs" style={{ padding: "6px 10px" }}
                  data-testid="simulateur-recharger"
                  onClick={() => (termine ? ouvrir(vue.personne, vue.profil) : setCle((k) => k + 1))}>
                  <RotateCw size={12} className="inline -mt-0.5 mr-1" />{termine ? "Rouvrir" : "Recharger"}
                </button>
                <button type="button" className="btn-ghost text-xs" style={{ padding: "6px 10px" }}
                  data-testid="simulateur-fermer" onClick={fermer}>
                  <X size={12} className="inline -mt-0.5 mr-1" />Fermer
                </button>
              </span>
            </div>
          )}
          <Telephone vue={vue} cle={cle} termine={termine} />
          {vue && (
            <p className="text-xs text-[var(--muted)] text-center max-w-md mx-auto">
              Un bouton qui enregistre (absence, appel, message lu, profil…) répond « Simulation : lecture
              seule ». L'ouverture de la simulation est notée dans le journal.
            </p>
          )}
        </div>
      </div>
    </Section>
  );
}
